/**
 * Messages for the authenticator-app steps of sign-in. Pure, so the sign-in
 * screen and its tests read them without the Cognito SDK.
 */

export const MFA_MESSAGES = {
  wrongCode: 'Incorrect code.',
  sessionExpired: 'Session expired. Sign in again.',
  tooManyAttempts: 'Too many attempts. Wait a moment and try again.',
  failed: 'Verification failed. Try again.',
  network: 'Cannot reach the sign-in service. Check the connection and try again.',
  newPasswordFailed: 'That password was not accepted.',
  newPasswordPolicy: 'Use at least 12 characters with upper case, lower case, a number and a symbol.',
};

function codeOf(error: unknown): string {
  const value = error as { code?: string; name?: string } | null;
  return value?.code ?? value?.name ?? '';
}

/** The Cognito session an MFA step answers on lasts three minutes. */
export function isMfaSessionExpired(error: unknown): boolean {
  const message = String((error as { message?: string } | null)?.message ?? '');
  return codeOf(error) === 'NotAuthorizedException' && /session/i.test(message);
}

export function mfaErrorMessage(error: unknown): string {
  if (isMfaSessionExpired(error)) return MFA_MESSAGES.sessionExpired;
  switch (codeOf(error)) {
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return MFA_MESSAGES.tooManyAttempts;
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
    case 'ExpiredCodeException':
      return MFA_MESSAGES.wrongCode;
    case 'NetworkError':
      return MFA_MESSAGES.network;
    default:
      return MFA_MESSAGES.failed;
  }
}

/** The new-password step, where a policy miss is the likely failure. */
export function newPasswordErrorMessage(error: unknown): string {
  if (isMfaSessionExpired(error)) return MFA_MESSAGES.sessionExpired;
  if (codeOf(error) === 'InvalidPasswordException') return MFA_MESSAGES.newPasswordPolicy;
  const message = mfaErrorMessage(error);
  return message === MFA_MESSAGES.failed ? MFA_MESSAGES.newPasswordFailed : message;
}
